/**
 * SyncIndicator - Shows WebSocket connection status in the top bar
 */

import React, { useState } from 'react';
import { Wifi, WifiOff } from 'lucide-react';
import { useUserWebSocket } from '../../contexts/UserWebSocketContext';
import type { WebSocketStatus } from '../../lib/userWebSocket';

export function SyncIndicator() {
  const { status, error } = useUserWebSocket();
  const [showTooltip, setShowTooltip] = useState(false);

  // Get color and icon based on status
  const getStatusConfig = (status: WebSocketStatus) => {
    switch (status) {
      case 'connected':
        return {
          color: 'text-green-500',
          dotColor: 'bg-green-500',
          text: 'Connected',
          icon: Wifi,
        };
      case 'connecting':
        return {
          color: 'text-yellow-500',
          dotColor: 'bg-yellow-500',
          text: 'Reconnecting...',
          icon: Wifi,
        };
      case 'disconnected':
      case 'error':
        return {
          color: 'text-red-500',
          dotColor: 'bg-red-500',
          text: status === 'error' ? 'Connection Error' : 'Disconnected',
          icon: WifiOff,
        };
    }
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-surface-800 border border-surface-600 cursor-default">
        <Icon className={`w-4 h-4 ${config.color}`} />
        <div className={`w-2 h-2 rounded-full ${config.dotColor} ${status === 'connecting' ? 'animate-pulse' : ''}`} />
      </div>

      {/* Hover Tooltip */}
      {showTooltip && (
        <div className="absolute right-0 top-full mt-2 z-50">
          <div className="bg-surface-900 border border-surface-600 rounded-lg shadow-xl p-3 min-w-[180px]">
            <div className="text-sm font-medium text-text-primary mb-2">
              Connection Status
            </div>

            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2 h-2 rounded-full ${config.dotColor}`} />
              <span className={`text-xs ${config.color}`}>
                {config.text}
              </span>
            </div>

            {error && (
              <div className="mt-2 pt-2 border-t border-surface-700">
                <p className="text-xs text-red-400">
                  {error.message === 'hard-refresh-required'
                    ? 'Sync gap detected - refresh required'
                    : error.message}
                </p>
              </div>
            )}

            {status === 'connected' && (
              <div className="mt-2 pt-2 border-t border-surface-700">
                <p className="text-xs text-text-muted">
                  Real-time sync active
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
